import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../../api'
import { Icon } from '../Icon'
import { FLAG_CATEGORIES, FLAG_CATEGORY_LABEL } from '../../types'
import type { FlagCategory } from '../../types'

// AlarmModal — 2×3 category-button grid (Live View PR L3 / spec 33 §6,
// v5 prototype L2739-2758). Sends the picked category as a kebab-case
// slug to POST /api/events/{id}/live-flags. UI renders the literal
// label strings from FLAG_CATEGORY_LABEL — never the slug.
//
// Public-contract copy strings (do not paraphrase, do not unicode-
// escape — engineering lessons §10 + work plan acceptance criteria):
//   - Heading: "Raise Alarm"
//   - Subtext: "Notify the on-call team and log an incident. Use only
//              when immediate attention is required."
//   - Six button labels: "Medical / First Aid", "Evacuation",
//     "Lost Child", "Crowd Control", "Train Issue", "Other"
//   - Send button: "Send Alarm"
//   - Success toast (raised by the parent): "Alarm sent — duty
//     manager notified" (literal em-dash).
//
// Visual scaffolding mirrors the prototype: oxblood-soft circle with
// siren, Playfair 22px heading, ghost-style category buttons aligned
// left, red Send Alarm button. The send is disabled until a category
// is picked — the prototype doesn't gate this but blind-sending an
// alarm with no category would be a footgun in production.

interface Props {
  eventId: number
  onClose: () => void
  onSent: (category: FlagCategory) => void
}

export function AlarmModal({ eventId, onClose, onSent }: Props) {
  const [selected, setSelected] = useState<FlagCategory | null>(null)
  const qc = useQueryClient()

  // Hooks always unconditional. Escape-to-close runs regardless of
  // mutation state. Body-scroll lock matches the ResourcesPanel pattern.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  const send = useMutation({
    mutationFn: () =>
      selected
        ? ApiClient.createLiveFlag(eventId, { category: selected })
        : Promise.reject(new Error('no category selected')),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['live-pulse', eventId] })
      qc.invalidateQueries({ queryKey: ['live-flags', eventId] })
      if (selected) onSent(selected)
      onClose()
    }
  })

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Raise alarm"
      className="fixed inset-0 z-[400] flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.7)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-parchment text-ink rounded-xl w-full"
        style={{ maxWidth: 480, padding: 28, boxShadow: '0 24px 60px -10px rgba(0,0,0,0.45)' }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="flex items-center justify-center bg-oxblood-soft"
            style={{ width: 44, height: 44, borderRadius: '50%', color: '#7a2329', flexShrink: 0 }}
          >
            <Icon name="siren" size={22} />
          </div>
          <h3 className="font-display" style={{ fontSize: 22, fontWeight: 600 }}>
            Raise Alarm
          </h3>
        </div>

        <p className="text-sm text-ink-soft mb-4">
          Notify the on-call team and log an incident. Use only when
          immediate attention is required.
        </p>

        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          {FLAG_CATEGORIES.map((slug) => {
            const active = selected === slug
            return (
              <button
                key={slug}
                type="button"
                onClick={() => setSelected(slug)}
                disabled={send.isPending}
                aria-pressed={active}
                className={
                  'flex items-center justify-start gap-2 px-3 py-2.5 rounded-md border text-sm font-semibold text-left transition-colors disabled:opacity-50 ' +
                  (active
                    ? 'bg-oxblood text-parchment border-oxblood'
                    : 'bg-white border-line text-ink hover:border-oxblood hover:text-oxblood')
                }
              >
                {FLAG_CATEGORY_LABEL[slug]}
              </button>
            )
          })}
        </div>

        {send.isError && (
          <div className="mt-3 bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
            Could not raise alarm: {(send.error as Error).message}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={send.isPending}
            className="px-4 py-2 rounded text-sm text-ink-soft hover:bg-white disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => send.mutate()}
            disabled={selected == null || send.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold bg-oxblood text-parchment hover:bg-oxblood/90 disabled:opacity-50"
          >
            <Icon name="siren" size={14} />
            {send.isPending ? 'Sending…' : 'Send Alarm'}
          </button>
        </div>
      </div>
    </div>
  )
}
